import './index.scss';
import { Button, Heading, SubHeading } from '../../components';
import { CheckCircle, Delete } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { ColorPicker, Select, SingleImage, TextInput } from '../../components/FormV2/components';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { metadataService } from '../../lib';
import addPending from '../../utils/addPending';
import removePending from '../../utils/removePending';
import { addError, addNotification } from '../../utils';
import { setDevelopmentMetaData, setMetaData } from '../../store/actions/general';
import { phaseSchema } from './phaseSchema';
import { useYupResolver } from '../../hooks';
import { getEnvironment, getStreetPins } from '../../store/actions/streetView';

const Phases = () => {
  // const { developmentId } = useParams();
  const { currentDevelopment } = useSelector((state) => state.general);
  const plots = useSelector((state) => state.plots);
  const phases = currentDevelopment?.phases ? currentDevelopment?.phases.filter(p => !p.default) : [];
  const [ mode, setMode ] = useState('view');
  const [ saving, setSaving ] = useState(false);
  const dispatch = useDispatch();
  const resolver = useYupResolver(phaseSchema);
  const { control, handleSubmit, watch, setValue, reset } = useForm({
    resolver,
    defaultValues: {
      name: 'Phase-',
    }
  });
  
  const nameValue = watch('name');

  const deletePhase = async(phaseId) => {
    let checkDependecy = plots.filter((plot) => plot.phase === phaseId);
    if (checkDependecy.length > 0) {
      addError(`This phase is used by ${checkDependecy.length} plots, please change the phase in plots first`);
      return;
    }
    const pending = addPending('Deleting phase');
    setSaving(true);
    await metadataService.deletePhase(phaseId).then(async (res) => {
      if (res) {
        await dispatch(getEnvironment());
        await dispatch(getStreetPins());
        await dispatch(setDevelopmentMetaData());
        setSaving(false);
        removePending(pending);
        addNotification('Phase deleted successfully');
      }
    }).catch((e) => {
      setSaving(false);
      removePending(pending);
      addError(e.message);
    });
  };

  const activePhase = async(phaseId) => {
    const pending = addPending('Activating phase');
    setSaving(true);
    await metadataService.activatePhase(phaseId).then(async(res) => {
      console.log(res);
      if (res) {
        await dispatch(getEnvironment());
        await dispatch(getStreetPins());
        dispatch(setMetaData(res));
        setSaving(false);
        removePending(pending);
        addNotification('Phase activated successfully');
      }
    }).catch((e) => {
      setSaving(false);
      removePending(pending);
      addError(e.message);
    });
  }

  const switchMode = (mode) => {
    if (mode === 'view') {
      setMode('create');
    } else {
      setMode('view');
    }
  };

  const onSubmit = async (data) => {
    setSaving(true);
    const pending = addPending('Creating phase');
    await metadataService.createPhase(data).then((res) => {
      console.log(res);
      if (res) {
        setSaving(false);
        removePending(pending);
        addNotification('Phase created successfully');
        dispatch(setMetaData(res));
        resetForm();
        setMode('view');
      }
    }).catch((e) => {
      setSaving(false);
      removePending(pending);
      addError(e.message);
    });
  };

  const resetForm = () => {
    reset({
      name: 'Phase-',
      mapType: 'beabloo',
      mediaUrl: '',
      mapBackgroundColor: '#ffffff',
      mapImage: null,
    })
  }

  useEffect(() => {
    if (nameValue && !nameValue.startsWith('Phase-')) {
      setValue('name', 'Phase-');
    }

    // Ensure a number follows 'Phase-'
    const phaseNumberMatch = nameValue.match(/^Phase-(\d+)$/);
    if (!phaseNumberMatch) {
      // If no number or incorrect format, reset to 'Phase-'
      setValue('name', 'Phase-');
    }
  }, [nameValue, setValue]);
  return (
    <div className='page-padding'>
      <div className='heading-with-button'>
        <div>
          <Heading label='Phases' />
          <SubHeading style={{ margin: 0 }}>Here you can create Phases</SubHeading>
        </div>
        <Button className={'shared-button'}
          onClick={() => switchMode(mode)}
          variant='contained'
          label= {mode === 'view' ? 'Create Phase' : 'Back'}
        />
      </div>
      <div className='parent-properties table-setting'>
        <div>
        {mode === 'create' && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{
                width: '50%', display: 'flex', flexDirection: 'column', gap: 1
              }}>
                <TextInput
                  full
                  name='name'
                  label='Name'
                  defaultValue={'Phase-'}
                  control={control}
                />
                <Select
                  name='mapType'
                  label='Map Type'
                  control={control}
                  options={[
                    { label: 'Basic', value: 'beabloo' },
                    { label: 'MultiFloor', value: 'multi-floor' },
                    { label: '3d Map', value: 'three-map' }
                  ]}
                  full
                />
                <TextInput
                  full
                  name='mediaUrl'
                  label='Media URL'
                  control={control}
                />
                <ColorPicker
                  full
                  name='mapBackgroundColor'
                  label='Map Background Color'
                  control={control}
                />
                <SingleImage
                  full
                  name='mapImage'
                  label='Map Image'
                  control={control}
                />
              </Box>
              <Button type='submit' label={'save'} />
            </Box>
          </form>
        )}
        </div>
        {mode === 'view' && (
          <div>
            <table>
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Phase</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
              {currentDevelopment?.phases.filter(p => !p.default)?.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center'}}>
                    No phases available. <a href='#' onClick={()=> setMode('create')}>Create ?</a>
                  </td>
                </tr>
              )}
              {phases.map(( phase, index) =>{
                return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{phase.name} {phase.default && `(default)`}</td>
                  <td className='action-icons'>
                    {!phase.default &&
                      <Delete 
                      className='delete-icon'
                      style={{ marginRight: '1rem', cursor: 'pointer' }}
                      onClick={() => deletePhase(phase.id)}
                      />
                    } 
                    <CheckCircle
                      onClick={() => activePhase(phase.id)}
                      style={{ 
                        color: currentDevelopment?.activePhase === phase.id ?
                        'green' : 'gray',
                        cursor: 'pointer'
                      }} />
                  </td>
                </tr>)
              })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Phases;
